// Libs
import { capitalize, intersection } from "lodash";
import type { DateRange } from "moment-range";
import moment, { type Moment } from "moment-timezone";

// Utils
import type { TFunction } from "src/design-system/core/TranslationContext";
import { oldFormatNumber } from "src/design-system/core/utils/formators";
import { getLocale } from "src/i18n";
import type { CheckboxElement } from "src/types/checkboxElement";
import type { DayOfTheWeek } from "src/types/enum/DayOfTheWeek";
import { LocaleEnum } from "src/types/enum/LanguageEnum";
import type { FormatterOptions } from "src/utils/formatters/common";
import { oldFormatFixedDecimal } from "src/utils/formatters/numbers";
import { getRoundedDate } from "../timeUtils";
import { FORMATTERS_TRANSLATION_NAMESPACE } from "./config";
import { getDPSuffixForSlotsFromString } from "./times";

export const DATES_TK = "DATES_TK";

export const FORMAT_DATE_STD_DATE_FNS = "yyyy-MM-dd";
export const FORMAT_DATE_STD = "YYYY-MM-DD";
export const FORMAT_DATE = "L";
export const FORMAT_TIME = "LT";
export const FORMAT_TIME_LONG = "LTS";

export const FORMAT_DATE_WITH_TIME = `${FORMAT_DATE} ${FORMAT_TIME}`;

// Custom format https://stackoverflow.com/questions/46311967/how-do-can-i-add-a-new-format-option-to-moment-js-locales
export const formatsDateShort: Record<LocaleEnum, string> = {
    [LocaleEnum.FR]: "DD MMM",
    [LocaleEnum.EN]: "DD MMM",
    [LocaleEnum.GB]: "DD MMM",
    [LocaleEnum.ES]: "D MMM",
};

export function formatDateShort(): string {
    return formatsDateShort[(moment.locale() as LocaleEnum).toLowerCase()];
}

export const formatsDateShortSlash: Record<LocaleEnum, string> = {
    [LocaleEnum.FR]: "DD/MM",
    [LocaleEnum.EN]: "DD/MM",
    [LocaleEnum.GB]: "DD/MM",
    [LocaleEnum.ES]: "DD/MM",
};
export function formatDateShortSlash(): string {
    return formatsDateShortSlash[(moment.locale() as LocaleEnum).toLowerCase()];
}

export const formatsDateDayDayMonth: Record<LocaleEnum, string> = {
    [LocaleEnum.FR]: "dddd DD MMMM",
    [LocaleEnum.EN]: "dddd, DD MMMM",
    [LocaleEnum.GB]: "dddd, DD MMMM",
    [LocaleEnum.ES]: "dddd, D [de] MMMM",
};
export function formatDateDayDayMonth(): string {
    return formatsDateDayDayMonth[(moment.locale() as LocaleEnum).toLowerCase()];
}

/**
 * Get localized day of week label
 * @param day ISO day of week between 0(Sunday) and 6(Saturday)
 * @returns Capitalized string of dow name
 */
export function getDowLabel(day: DayOfTheWeek, shortLabel: boolean): string {
    const locale = getLocale().split("-")[0];
    return capitalize(
        moment()
            .locale(locale)
            .isoWeekday(day)
            .format(shortLabel ? "ddd" : "dddd")
    );
}

export const daysOfTheWeek = ({
    shortLabel = false,
}: {
    shortLabel?: boolean;
} = {}): CheckboxElement[] => [
    {
        key: 1,
        name: "1",
        label: getDowLabel(1, shortLabel),
    },
    {
        key: 2,
        name: "2",
        label: getDowLabel(2, shortLabel),
    },
    {
        key: 3,
        name: "3",
        label: getDowLabel(3, shortLabel),
    },
    {
        key: 4,
        name: "4",
        label: getDowLabel(4, shortLabel),
    },
    {
        key: 5,
        name: "5",
        label: getDowLabel(5, shortLabel),
    },
    {
        key: 6,
        name: "6",
        label: getDowLabel(6, shortLabel),
    },
    {
        key: 0,
        name: "0",
        label: getDowLabel(0, shortLabel),
    },
];

/**
 * Format a date
 * @param date Date to format
 * @param format Format to use
 * @returns Formatted date
 */
export function formatDate(
    date: moment.MomentInput,
    format = FORMAT_DATE,
    { withBrowserTimeZone = false }: { withBrowserTimeZone?: boolean } = {}
): string {
    if (withBrowserTimeZone) {
        // applique la timezone du navigateur
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        return moment(date).tz(tz).format(format);
    }
    return moment(date).format(format);
}

/**
 * Get the day of the week from a given date
 * @param date Date to extract
 * @returns DoW
 */
export function getDowOfDate(date: moment.MomentInput): number {
    return moment(date).day();
}

/**
 * Format a number of hours into a string
 * @param hours Number of hours
 * @param options Format options
 * @returns Formatted string
 */
export function formatHour(
    hours: number,
    {
        decimalCount = 0,
        showHyphenIfZero = false,
    }: { decimalCount?: number; showHyphenIfZero?: boolean } = {}
): string {
    if (!hours === undefined || (hours === 0 && showHyphenIfZero)) {
        return "—";
    }

    return `${oldFormatFixedDecimal(hours, { decimalCount, showHyphenIfZero })}h`;
}

/**
 * Format a number of hours of uptime into a string
 * @param hours Number of hours
 * @returns Formatted string
 */
export function formatUptime(hours: number): string {
    if (!hours === undefined) {
        return "—";
    } else if (hours === 0) {
        return "<1h";
    }

    return `${oldFormatNumber(hours)}h`;
}

// Common code of formatSlotStringWithTimeZone and formatSlotStringWithTimeZoneWithWordLink
function commonSlotStringWithTimeZone(
    start: string,
    end: string,
    shifted: string,
    timezone: string,
    {
        translateCommon,
        hasRoundDate = true,
    }: {
        translateCommon: TFunction;
        hasRoundDate?: boolean;
    }
) {
    const startingAt = moment.tz(start, timezone).format(FORMAT_TIME);
    const endingAt = moment
        .tz(hasRoundDate ? getRoundedDate(30, moment(end).toDate()) : end, timezone)
        .format(FORMAT_TIME);

    const startDP1 = getDPSuffixForSlotsFromString(start, shifted, timezone, translateCommon);
    const endDP1 = getDPSuffixForSlotsFromString(end, shifted, timezone, translateCommon);
    return { startingAt, endingAt, startDP1, endDP1 };
}

/**
 * Format two times into a slot using timezone
 * @param start Start time
 * @param end End time
 * @param timezone Timezone to use
 * @returns Slot
 */
export function formatSlotStringWithTimeZone(
    start: string,
    end: string,
    shifted: string,
    timezone: string,
    {
        translateCommon,
        translateRaw,
        hasRoundDate = true,
        inProgressEnd = false,
        translationInProgressKey = `${FORMATTERS_TRANSLATION_NAMESPACE}:${DATES_TK}.in-progress`,
    }: {
        translateCommon: TFunction;
        translateRaw: TFunction;
        hasRoundDate?: boolean;
        inProgressEnd?: boolean;
        translationInProgressKey?: string;
    }
): string {
    const { startingAt, endingAt, startDP1, endDP1 } = commonSlotStringWithTimeZone(
        start,
        end,
        shifted,
        timezone,
        {
            translateCommon,
            hasRoundDate,
        }
    );
    return `${startingAt}${startDP1} - ${
        inProgressEnd ? translateRaw(translationInProgressKey) : `${endingAt}${endDP1}`
    }`;
}

/**
 * Same function as formatSlotStringWithTimeZone but with word link
 * @param start Start time
 * @param end End time
 * @param timezone Timezone to use
 * @returns Slot
 */
export function formatSlotStringWithTimeZoneWithWordLink(
    start: string,
    end: string,
    shifted: string,
    timezone: string,
    {
        translateCommon,
        translateRaw,
        hasRoundDate = true,
        inProgressEnd = false,
        translationInProgressKey = `${FORMATTERS_TRANSLATION_NAMESPACE}:${DATES_TK}.in-progress`,
    }: {
        translateCommon: TFunction;
        translateRaw: TFunction;
        hasRoundDate?: boolean;
        inProgressEnd?: boolean;
        translationInProgressKey?: string;
    }
): string {
    const { startingAt, endingAt, startDP1, endDP1 } = commonSlotStringWithTimeZone(
        start,
        end,
        shifted,
        timezone,
        {
            translateCommon,
            hasRoundDate,
        }
    );

    return `${startingAt}${startDP1} ${translateCommon("word-link.to-hour")} ${
        inProgressEnd ? translateRaw(translationInProgressKey) : `${endingAt}${endDP1}`
    }`;
}

/**
 * Format two times into a slot using timezone
 * @param startTime Start time
 * @param endTime End time
 * @param timezone Timezone to use
 * @returns Slot
 */
export function formatTimeSlotsWithTimeZone(
    startTime: string,
    endTime: string,
    timezone: string
): string {
    const startDate = moment.tz(startTime, FORMAT_TIME_LONG, timezone).format(FORMAT_TIME);
    const endDate = moment.tz(endTime, FORMAT_TIME_LONG, timezone).format(FORMAT_TIME);

    return `${startDate} - ${endDate}`;
}

/**
 * Format a date with timezone
 * @param date Date to format
 * @param timezone Timezone to use
 * @param format Format to use
 * @returns Formatted date
 */
export function formatDateWithTimeZone(
    date: moment.MomentInput,
    timezone: string,
    format = FORMAT_DATE
): string {
    return moment.tz(date, timezone).format(format);
}

/**
 * Format end date or show non stop label if not defined
 * @param date End date or null if non stop action
 * @param timezone Timezone to use
 * @param options Options required to format date
 * @returns Formatted date or non stop
 */
export function formatEndDate(
    date: moment.MomentInput,
    timezone: string,
    { translateRaw: t }: FormatterOptions
): string {
    if (!date) {
        return t(`${FORMATTERS_TRANSLATION_NAMESPACE}:${DATES_TK}.non-stop`);
    }

    if (date && timezone) {
        return formatDateWithTimeZone(date, timezone);
    }

    return formatDate(date);
}

/**
 * Get the day of the week from a given date with timezone
 * @param date Date to extract
 * @param timezone Timezone to use
 * @returns DoW
 */
export function getDowOfDateWithTimeZone(date: moment.MomentInput, timezone: string): number {
    return moment.tz(date, timezone).day();
}

/**
 * Get the now date from timezone
 * @param timezone Timezone to use
 * @returns now at timezone time
 */
export function getNowWithTimeZone(timezone: string): Moment {
    return moment.tz(moment().utc(), timezone);
}

/**
 * Convert date UTC 0 to given timezone
 * @param timezone Timezone to use
 * @param date Date to convert
 * @returns Moment date at timezone time
 */
export function getDateWithTimeZone(date: moment.MomentInput, timezone: string): Moment {
    return moment.tz(date, timezone);
}

/**
 * Convert date at string format to date in timezone
 * @param timezone Timezone to use
 * @param date string to convert
 * @returns Moment date at timezone time
 */
export function getDateStringWithTimeZone(date: string, timezone: string): Moment {
    return moment.tz(date, timezone);
}

/**
 * Format the day of the week
 * @param date Date to format
 * @returns Day of the week
 */
export function formatDayOfWeek(date: moment.MomentInput | number | Date, short = false): string {
    const day = Number.isInteger(date) ? Number(date) : moment(date).day();
    return getDowLabel(day, short);
}

/**
 * Format an array of days of the week
 * @param days Days to format
 * @returns Days of the week joined by comma
 */
export function formatDaysOfWeek(days: DayOfTheWeek[]): string {
    return orderDays(days)
        .map((dow) => getDowLabel(dow, false))
        .join(", ");
}

/**
 * Format a date range
 * @param range Date range to format
 * @param opts Options for format
 * @returns Formatted string
 */
export function formatDateRange(
    range: DateRange,
    {
        translateCommon,
        dateFormat = formatDateShort(),
        translateKey = "a-to-b",
    }: { translateCommon: TFunction; dateFormat?: string; translateKey?: "a-to-b" | "from-a-to-b" }
): string {
    if (range.start.isSame(range.end)) {
        return range.start.format(dateFormat);
    }

    return translateCommon(`dates.range.${translateKey}`, {
        from: range.start.format(dateFormat),
        to: range.end.format(dateFormat),
    });
}

const DAYS_ORDER = [1, 2, 3, 4, 5, 6, 0];

/**
 * Orders days of the week
 * @param days Days to order
 * @returns Ordered days
 */
export function orderDays(days: number[]): number[] {
    return intersection(DAYS_ORDER, days);
}
